import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  addDoc,
  serverTimestamp,
  query,
  where,
} from "firebase/firestore";
import {
  getStorage,
  ref,
  deleteObject,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { db } from "../../firebaseConfig";
import AccountingShell from "./AccountingShell";
import toast from "react-hot-toast";

const GST_RATE = 18;
const DEFAULT_TDS_RATE = 1;
const ACCOUNTS_EMAIL = "accounts@shineagri.org";

const INR = (n) =>
  `₹ ${Number(n || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;

const num = (v) => Number(v || 0);

const toDateInput = (v) => {
  try {
    const d = v?.toDate ? v.toDate() : v ? new Date(v) : new Date();
    return d && !Number.isNaN(d.getTime()) ? d.toISOString().slice(0, 10) : "";
  } catch {
    return "";
  }
};

const fmtDate = (v) => {
  try {
    const d = v?.toDate ? v.toDate() : v ? new Date(v) : null;
    return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString("en-IN") : "—";
  } catch {
    return "—";
  }
};

const billNo = (r) =>
  r.raBillNo ||
  r.billNo ||
  r.invoiceNo ||
  r.raNo ||
  `RA-${String(r.id || "").slice(0, 8).toUpperCase()}`;

const billPdfUrl = (r) =>
  r.approvedRaBillPdfUrl ||
  r.raBillPdfUrl ||
  r.pdfUrl ||
  r.billPdfUrl ||
  r.invoicePdfUrl ||
  r.fileUrl ||
  r.downloadURL ||
  r.downloadUrl ||
  r.url ||
  "";

const makePaymentVoucher = (r) =>
  `PAY-${String(r.raBillNo || r.displayBillNo || r.id).replace(/[\/\\]/g, "-")}`;

const getTenderBaseAmountWithoutGst = (tender, bill) =>
  num(
    tender.baseAmountWithoutGst ||
      tender.tenderBaseAmountWithoutGst ||
      tender.estimatedAmountWithoutGst ||
      tender.baseAmount ||
      tender.amount ||
      bill.tenderAmountWithoutGst ||
      bill.tenderAmount ||
      0
  );

const getL1AmountWithoutGst = (tender, bill) =>
  num(
    tender.awardedAmountWithoutGst ||
      tender.l1AmountWithoutGst ||
      tender.awardedAmount ||
      tender.tenderAwardAmount ||
      tender.awardedValue ||
      bill.l1AmountWithoutGst ||
      bill.l1Amount ||
      0
  );

const getDelayPenaltyFromTender = (tender) => {
  if (!tender?.workStartDate || !tender?.workDurationDays) {
    return { overdueDays: 0, penalty: 0 };
  }

  const start = new Date(tender.workStartDate);
  const today = new Date();

  start.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  const passedDays = Math.floor((today - start) / (1000 * 60 * 60 * 24));
  const overdueDays = passedDays - Number(tender.workDurationDays || 0);

  if (overdueDays <= 0) return { overdueDays: 0, penalty: 0 };

  const awardAmount = Number(
    tender.awardedAmountWithoutGst ||
      tender.l1AmountWithoutGst ||
      tender.awardedAmount ||
      tender.tenderAwardAmount ||
      tender.awardedValue ||
      0
  );

  const fullWeeks = Math.floor(overdueDays / 7);
  const penalty = Math.min(awardAmount * (fullWeeks * 0.01), awardAmount * 0.05);

  return { overdueDays, penalty };
};

export default function RABillVerify() {
  return (
    <AccountingShell title="RA Bill Accounting">
      <RABillContent />
    </AccountingShell>
  );
}

function RABillContent({ me = {} }) {
  const storage = getStorage();

  const [raBills, setRaBills] = useState([]);
  const [tenders, setTenders] = useState([]);
  const [paymentVouchers, setPaymentVouchers] = useState([]);

  const [selectedTenderId, setSelectedTenderId] = useState("");
  const [selectedVendorUid, setSelectedVendorUid] = useState("");
  const [selectedBill, setSelectedBill] = useState(null);
  const [historyBill, setHistoryBill] = useState(null);
  const [search, setSearch] = useState("");

  const load = async () => {
    const [raSnap, tenderSnap, voucherSnap] = await Promise.all([
      getDocs(collection(db, "ra_bills")),
      getDocs(collection(db, "tenders")),
      getDocs(collection(db, "ra_payment_vouchers")).catch(() => ({ docs: [] })),
    ]);

    setRaBills(raSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setTenders(tenderSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setPaymentVouchers(voucherSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  useEffect(() => {
    load();
  }, []);

  const paymentHistoryMap = useMemo(() => {
    const map = {};

    paymentVouchers.forEach((v) => {
      (v.allocations || []).forEach((a) => {
        if (!a.raBillId) return;
        if (!map[a.raBillId]) map[a.raBillId] = [];

        map[a.raBillId].push({
          voucherId: v.id,
          voucherNo: v.voucherNo,
          paymentDate: v.paymentDate,
          paymentUtr: v.paymentUtr,
          allocationMode: v.allocationMode,
          adjustedAmount: num(a.adjustedAmount),
          outstandingBefore: num(a.outstandingBefore),
          outstandingAfter: num(a.outstandingAfter),
          invoiceAmount: num(a.invoiceAmount),
          status: a.status,
          createdAt: v.createdAt,
        });
      });
    });

    Object.keys(map).forEach((id) => {
      map[id].sort((a, b) => {
        const ad = a.createdAt?.seconds || new Date(a.paymentDate || 0).getTime();
        const bd = b.createdAt?.seconds || new Date(b.paymentDate || 0).getTime();
        return ad - bd;
      });
    });

    return map;
  }, [paymentVouchers]);

  const baseRows = useMemo(() => {
    return raBills.map((r) => {
      const tender = tenders.find((t) => t.id === r.tenderId) || {};

      const tenderAmountWithoutGst = getTenderBaseAmountWithoutGst(tender, r);
      const l1AmountWithoutGst = getL1AmountWithoutGst(tender, r);

      const delayPenalty = getDelayPenaltyFromTender(tender);

      const basic = num(r.basicAmount || r.totalAmount || r.grossAmount);
      const gst = Math.round((basic * GST_RATE) / 100);
      const gross = basic + gst;

      const paymentHistory = paymentHistoryMap[r.id] || [];
      const historyPaid = paymentHistory.reduce((s, h) => s + num(h.adjustedAmount), 0);
      const paidAmount = Math.max(
        num(r.totalPaidAmount || r.allocatedPaidAmount || r.paymentAmount),
        historyPaid
      );

      return {
        ...r,
        tender,

        basic,
        gst,
        gross,

        tenderAmountWithoutGst,
        l1AmountWithoutGst,
        l1GstAmount: Math.round((l1AmountWithoutGst * GST_RATE) / 100),
        l1WithGst:
          l1AmountWithoutGst + Math.round((l1AmountWithoutGst * GST_RATE) / 100),

        dashboardOverdueDays: delayPenalty.overdueDays,
        dashboardPenalty: num(r.dashboardPenalty || r.contractorDashboardPenalty || delayPenalty.penalty),

        paidAmount,
        paymentHistory,

        displayBillNo: billNo(r),
        displayPdfUrl: billPdfUrl(r),
        raInvoiceNo: r.raInvoiceNo || r.invoiceNo || billNo(r),
        raInvoiceDate: r.raInvoiceDate || r.invoiceDate || r.raDate || r.createdAt,

        pacsName:
          tender.ownerCompany ||
          tender.pacsName ||
          tender.projectName ||
          tender.title ||
          r.projectName ||
          "Unknown Project",

        contractorName:
          r.vendorName ||
          r.contractorName ||
          tender.awardedVendorName ||
          r.vendorUid ||
          "Unknown Contractor",

        contractorEmail:
          r.vendorEmail ||
          r.contractorEmail ||
          tender.awardedVendorEmail ||
          r.email ||
          "",
      };
    });
  }, [raBills, tenders, paymentHistoryMap]);

  const rows = useMemo(() => {
    return baseRows.map((r) => {
      const approvedBills = baseRows.filter(
        (x) =>
          x.tenderId === r.tenderId &&
          x.vendorUid === r.vendorUid &&
          x.accountingStatus === "CEO_APPROVED" &&
          x.id !== r.id
      );

      const alreadyApprovedBasicWithoutGst = approvedBills.reduce(
        (s, x) => s + num(x.approvedBasicAfterL1 || x.basicAmount || x.basic || x.totalAmount),
        0
      );

      const alreadyApprovedNet = approvedBills.reduce(
        (s, x) => s + num(x.netPayable || x.net),
        0
      );

      const extraWorkAmount = num(r.extraWorkAmount);
      const extraWorkGst = Math.round((extraWorkAmount * GST_RATE) / 100);

      const deduction = num(r.deductionAmount);
      const retention = num(r.retentionAmount);

      const tdsRate = num(r.tdsRate || DEFAULT_TDS_RATE);
      const tdsManualAmount = num(r.tdsManualAmount);
      const tds = num(
        r.tdsAmount || tdsManualAmount || Math.round((r.basic * tdsRate) / 100)
      );

      const manualPenalty = num(r.penaltyAmount);

      const ceoPenaltyApproved = Boolean(r.ceoPenaltyApproved);
      const finalPenaltyAmount = ceoPenaltyApproved
        ? num(r.finalPenaltyAmount || r.dashboardPenalty)
        : 0;

      const totalPenalty = manualPenalty + finalPenaltyAmount;

      const l1CutWithoutGst =
        r.l1AmountWithoutGst > 0
          ? Math.max(alreadyApprovedBasicWithoutGst + r.basic - r.l1AmountWithoutGst, 0)
          : 0;

      const approvedBasicAfterL1 = Math.max(r.basic - l1CutWithoutGst, 0);
      const gstOnApprovedBasic = Math.round((approvedBasicAfterL1 * GST_RATE) / 100);

      const payableBeforeDeduction =
        approvedBasicAfterL1 + gstOnApprovedBasic + extraWorkAmount + extraWorkGst;

      const net = Math.max(
        payableBeforeDeduction - tds - deduction - retention - totalPenalty,
        0
      );

      const outstandingAmount =
        r.outstandingAmount !== undefined
          ? num(r.outstandingAmount)
          : Math.max(net - num(r.paidAmount), 0);

      const paymentStatus =
        r.paidAmount > 0 && outstandingAmount <= 0
          ? "FULLY_PAID"
          : r.paidAmount > 0
          ? "PART_PAID"
          : r.paymentStatus || "PENDING";

      return {
        ...r,

        alreadyApprovedBasicWithoutGst,
        alreadyApprovedNet,

        extraWorkAmount,
        extraWorkGst,

        deduction,
        retention,

        tdsRate,
        tdsManualAmount,
        tds,

        manualPenalty,
        ceoPenaltyApproved,
        finalPenaltyAmount,
        totalPenalty,

        l1CutWithoutGst,
        l1Cut: l1CutWithoutGst,
        approvedBasicAfterL1,
        gstOnApprovedBasic,

        payableBeforeDeduction,
        net,

        outstandingAmount,
        paymentStatus,
      };
    });
  }, [baseRows]);

  const filteredRows = rows.filter((r) =>
    [
      r.pacsName,
      r.contractorName,
      r.displayBillNo,
      r.tenderId,
      r.raInvoiceNo,
      r.paymentStatus,
      r.accountingStatus,
      ...(r.paymentHistory || []).map((h) => `${h.voucherNo} ${h.paymentUtr}`),
    ]
      .join(" ")
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  const getAlreadyApprovedBasicWithoutGst = (r) =>
    rows
      .filter(
        (x) =>
          x.tenderId === r.tenderId &&
          x.vendorUid === r.vendorUid &&
          x.accountingStatus === "CEO_APPROVED" &&
          x.id !== r.id
      )
      .reduce((s, x) => s + num(x.approvedBasicAfterL1 || x.basic), 0);

  const getAlreadyApprovedNet = (r) =>
    rows
      .filter(
        (x) =>
          x.tenderId === r.tenderId &&
          x.vendorUid === r.vendorUid &&
          x.accountingStatus === "CEO_APPROVED" &&
          x.id !== r.id
      )
      .reduce((s, x) => s + num(x.netPayable || x.net), 0);
        const safeAddLedger = async (payload) => {
    const q = query(
      collection(db, "contractor_ledgers"),
      where("raBillId", "==", payload.raBillId || ""),
      where("type", "==", payload.type),
      where("voucherNo", "==", payload.voucherNo)
    );

    const snap = await getDocs(q);
    if (!snap.empty) return false;

    await addDoc(collection(db, "contractor_ledgers"), {
      ...payload,
      createdAt: serverTimestamp(),
      createdBy: me.uid || "",
      createdByRole: me.role || "",
    });

    return true;
  };

  const queueMail = async ({ to, subject, body, type, data }) => {
    await addDoc(collection(db, "mail_queue"), {
      to,
      subject,
      body,
      type,
      status: "PENDING",
      data,
      createdAt: serverTimestamp(),
      createdBy: me.uid || "",
    });
  };

  const projectRows = useMemo(() => {
    const map = {};

    filteredRows.forEach((r) => {
      if (!map[r.tenderId]) {
        map[r.tenderId] = {
          tenderId: r.tenderId,
          pacsName: r.pacsName,
          tenderAmountWithoutGst: r.tenderAmountWithoutGst,
          l1AmountWithoutGst: r.l1AmountWithoutGst,
          l1GstAmount: r.l1GstAmount,
          l1WithGst: r.l1WithGst,
          raCount: 0,
          pendingCount: 0,
          verifiedCount: 0,
          approvedCount: 0,
          basic: 0,
          approvedBasic: 0,
          gst: 0,
          extraWork: 0,
          extraWorkGst: 0,
          tds: 0,
          dashboardPenalty: 0,
          finalPenalty: 0,
          l1Cut: 0,
          net: 0,
          paid: 0,
          outstanding: 0,
          approvedAmount: 0,
        };
      }

      map[r.tenderId].raCount += 1;
      map[r.tenderId].basic += r.basic;
      map[r.tenderId].approvedBasic += r.approvedBasicAfterL1;
      map[r.tenderId].gst += r.gstOnApprovedBasic;
      map[r.tenderId].extraWork += r.extraWorkAmount;
      map[r.tenderId].extraWorkGst += r.extraWorkGst;
      map[r.tenderId].tds += r.tds;
      map[r.tenderId].dashboardPenalty += r.dashboardPenalty;
      map[r.tenderId].finalPenalty += r.finalPenaltyAmount;
      map[r.tenderId].l1Cut += r.l1Cut;
      map[r.tenderId].net += r.net;
      map[r.tenderId].paid += r.paidAmount;
      map[r.tenderId].outstanding += r.outstandingAmount;

      if (r.accountingStatus === "ACCOUNTANT_VERIFIED") {
        map[r.tenderId].verifiedCount += 1;
      } else if (r.accountingStatus === "CEO_APPROVED") {
        map[r.tenderId].approvedCount += 1;
        map[r.tenderId].approvedAmount += num(r.netPayable || r.net);
      } else {
        map[r.tenderId].pendingCount += 1;
      }
    });

    return Object.values(map);
  }, [filteredRows]);

  const contractorRows = useMemo(() => {
    const map = {};

    filteredRows
      .filter((r) => r.tenderId === selectedTenderId)
      .forEach((r) => {
        const key = r.vendorUid || r.contractorName || r.vendorName;

        if (!map[key]) {
          map[key] = {
            vendorUid: key,
            contractorName: r.contractorName,
            raCount: 0,
            net: 0,
            paid: 0,
            outstanding: 0,
            approvedAmount: 0,
            pendingAmount: 0,
            l1AmountWithoutGst: r.l1AmountWithoutGst,
          };
        }

        map[key].raCount += 1;
        map[key].net += r.net;
        map[key].paid += r.paidAmount;
        map[key].outstanding += r.outstandingAmount;

        if (r.accountingStatus === "CEO_APPROVED") {
          map[key].approvedAmount += num(r.netPayable || r.net);
        } else {
          map[key].pendingAmount += r.net;
        }
      });

    return Object.values(map);
  }, [filteredRows, selectedTenderId]);

  const billRows = filteredRows.filter(
    (r) =>
      r.tenderId === selectedTenderId &&
      (r.vendorUid === selectedVendorUid ||
        r.contractorName === selectedVendorUid ||
        r.vendorName === selectedVendorUid)
  );

  const totals = useMemo(
    () => ({
      projects: projectRows.length,
      bills: filteredRows.length,
      basic: filteredRows.reduce((s, r) => s + r.basic, 0),
      approvedBasic: filteredRows.reduce((s, r) => s + r.approvedBasicAfterL1, 0),
      gst: filteredRows.reduce((s, r) => s + r.gstOnApprovedBasic, 0),
      extraWork: filteredRows.reduce((s, r) => s + r.extraWorkAmount, 0),
      tds: filteredRows.reduce((s, r) => s + r.tds, 0),
      dashboardPenalty: filteredRows.reduce((s, r) => s + r.dashboardPenalty, 0),
      finalPenalty: filteredRows.reduce((s, r) => s + r.finalPenaltyAmount, 0),
      l1Cut: filteredRows.reduce((s, r) => s + r.l1Cut, 0),
      approved: filteredRows
        .filter((r) => r.accountingStatus === "CEO_APPROVED")
        .reduce((s, r) => s + num(r.netPayable || r.net), 0),
      pending: filteredRows
        .filter((r) => r.accountingStatus !== "CEO_APPROVED")
        .reduce((s, r) => s + r.net, 0),
      paid: filteredRows.reduce((s, r) => s + r.paidAmount, 0),
      outstanding: filteredRows.reduce((s, r) => s + r.outstandingAmount, 0),
    }),
    [filteredRows, projectRows]
  );

  const sendCorrection = async (r) => {
    const remark = window.prompt("Correction remark likho:");
    if (!remark) return;

    await updateDoc(doc(db, "ra_bills", r.id), {
      accountingStatus: "CORRECTION_REQUIRED",
      accountCorrectionRemark: remark,
      correctionRaisedBy: me.uid || "",
      correctionRaisedAt: serverTimestamp(),
    });

    toast.success("Correction sent");
    load();
  };

  const editOldInvoice = async (r) => {
    if (!["ACCOUNTANT", "ADMIN"].includes(me.role)) {
      toast.error("Only Accountant/Admin allowed.");
      return;
    }

    const invoiceNo = window.prompt("RA Invoice / Bill No:", r.raInvoiceNo || r.displayBillNo);
    if (!invoiceNo) return;

    const invoiceDate = window.prompt("Invoice Date YYYY-MM-DD:", toDateInput(r.raInvoiceDate));
    if (!invoiceDate) return;

    const basic = num(window.prompt("RA Basic Amount WITHOUT GST:", String(r.basic)));
    const extraWorkAmount = num(
      window.prompt("Extra Work Amount WITHOUT GST (+):", String(r.extraWorkAmount || 0))
    );

    const tdsRate = num(window.prompt("TDS % on basic without GST:", String(r.tdsRate || DEFAULT_TDS_RATE)));

    const tdsManualText = window.prompt(
      "TDS exact amount manual डालना है तो amount लिखो, नहीं तो blank रखो:",
      r.tdsManualAmount ? String(r.tdsManualAmount) : ""
    );

    const tdsManualAmount = tdsManualText === "" ? 0 : num(tdsManualText);

    const deductionAmount = num(window.prompt("Other Deduction:", String(r.deduction || 0)));
    const retentionAmount = num(window.prompt("Retention Amount:", String(r.retention || 0)));
    const penaltyAmount = num(window.prompt("Manual Penalty Amount:", String(r.manualPenalty || 0)));

    const gst = Math.round((basic * GST_RATE) / 100);
    const gross = basic + gst;
    const extraWorkGst = Math.round((extraWorkAmount * GST_RATE) / 100);

    const tdsAmount =
      tdsManualAmount > 0 ? tdsManualAmount : Math.round((basic * tdsRate) / 100);

    const alreadyApprovedBasicWithoutGst = getAlreadyApprovedBasicWithoutGst(r);

    const l1CutWithoutGst =
      r.l1AmountWithoutGst > 0
        ? Math.max(alreadyApprovedBasicWithoutGst + basic - r.l1AmountWithoutGst, 0)
        : 0;

    const approvedBasicAfterL1 = Math.max(basic - l1CutWithoutGst, 0);
    const gstOnApprovedBasic = Math.round((approvedBasicAfterL1 * GST_RATE) / 100);

    const payableBeforeDeduction =
      approvedBasicAfterL1 + gstOnApprovedBasic + extraWorkAmount + extraWorkGst;

    const finalPenaltyAmount = r.ceoPenaltyApproved
      ? num(r.finalPenaltyAmount || r.dashboardPenalty)
      : 0;

    const netPayable = Math.max(
      payableBeforeDeduction -
        tdsAmount -
        deductionAmount -
        retentionAmount -
        penaltyAmount -
        finalPenaltyAmount,
      0
    );

    await updateDoc(doc(db, "ra_bills", r.id), {
      raBillNo: invoiceNo,
      raInvoiceNo: invoiceNo,
      raInvoiceDate: invoiceDate,
      invoiceDate,

      basicAmount: basic,
      gstRate: GST_RATE,
      gstAmount: gst,
      grossWithGST: gross,

      extraWorkAmount,
      extraWorkGst,

      tdsRate,
      tdsManualAmount,
      tdsAmount,

      deductionAmount,
      retentionAmount,
      penaltyAmount,

      dashboardPenalty: r.dashboardPenalty,
      finalPenaltyAmount,

      tenderAmountWithoutGst: r.tenderAmountWithoutGst,
      l1AmountWithoutGst: r.l1AmountWithoutGst,
      l1GstAmount: r.l1GstAmount,
      l1WithGst: r.l1WithGst,

      l1CutAmount: l1CutWithoutGst,
      l1CutWithoutGst,
      approvedBasicAfterL1,
      gstOnApprovedBasic,
      payableBeforeDeduction,
      netPayable,

      manualOldInvoiceUpdated: true,
      manualOldInvoiceUpdatedBy: me.uid || "",
      manualOldInvoiceUpdatedAt: serverTimestamp(),
    });

    toast.success("Invoice updated: L1 cut + GST extra + TDS without GST + penalty control.");
    load();
  };

  const approveOrAdjustPenaltyByCEO = async (r) => {
    if (!["CEO", "ADMIN"].includes(me.role)) {
      toast.error("Only CEO/Admin allowed.");
      return;
    }

    const suggested = num(r.dashboardPenalty || 0);

    const penaltyText = window.prompt(
      `Dashboard penalty: ${INR(suggested)}\nCEO final penalty amount डालो. Zero भी रख सकते हो:`,
      String(r.finalPenaltyAmount || suggested || 0)
    );

    if (penaltyText === null) return;

    const finalPenaltyAmount = num(penaltyText);

    const netAfterPenalty = Math.max(
      r.payableBeforeDeduction -
        r.tds -
        r.deduction -
        r.retention -
        r.manualPenalty -
        finalPenaltyAmount,
      0
    );

    await updateDoc(doc(db, "ra_bills", r.id), {
      ceoPenaltyApproved: true,
      finalPenaltyAmount,
      netPayable: netAfterPenalty,
      penaltyApprovedBy: me.uid || "",
      penaltyApprovedAt: serverTimestamp(),
      penaltyApprovalRemark:
        finalPenaltyAmount === 0
          ? "CEO waived/reduced penalty to zero"
          : "CEO approved penalty amount",
    });

    await queueMail({
      to: [ACCOUNTS_EMAIL, r.contractorEmail].filter(Boolean).join(","),
      subject: `Penalty Approved/Adjusted - ${r.raInvoiceNo || r.displayBillNo}`,
      type: "RA_BILL_PENALTY_APPROVED",
      body: `CEO penalty decision updated.

Invoice: ${r.raInvoiceNo || r.displayBillNo}
Dashboard Penalty: ${INR(suggested)}
Final Approved Penalty: ${INR(finalPenaltyAmount)}
Net Payable after penalty: ${INR(netAfterPenalty)}`,
      data: { raBillId: r.id, tenderId: r.tenderId, vendorUid: r.vendorUid },
    });

    toast.success("Penalty approved/adjusted by CEO.");
    load();
  };
    const accountantVerify = async (r) => {
    if (!["ACCOUNTANT", "ADMIN"].includes(me.role)) {
      toast.error("Only Accountant/Admin allowed.");
      return;
    }

    const invoiceNo = window.prompt("RA Invoice / Bill No:", r.raInvoiceNo || r.displayBillNo);
    if (!invoiceNo) return;

    const invoiceDate = window.prompt("Invoice Date YYYY-MM-DD:", toDateInput(r.raInvoiceDate));
    if (!invoiceDate) return;

    await updateDoc(doc(db, "ra_bills", r.id), {
      accountingStatus: "ACCOUNTANT_VERIFIED",

      raBillNo: invoiceNo,
      raInvoiceNo: invoiceNo,
      raInvoiceDate: invoiceDate,
      invoiceDate,

      basicAmount: r.basic,
      gstRate: GST_RATE,
      gstAmount: r.gst,
      grossWithGST: r.gross,

      tenderAmountWithoutGst: r.tenderAmountWithoutGst,
      l1AmountWithoutGst: r.l1AmountWithoutGst,
      l1GstAmount: r.l1GstAmount,
      l1WithGst: r.l1WithGst,

      alreadyApprovedBasicWithoutGst: getAlreadyApprovedBasicWithoutGst(r),
      alreadyApprovedNet: getAlreadyApprovedNet(r),

      l1CutAmount: r.l1Cut,
      l1CutWithoutGst: r.l1CutWithoutGst,
      approvedBasicAfterL1: r.approvedBasicAfterL1,
      gstOnApprovedBasic: r.gstOnApprovedBasic,

      extraWorkAmount: r.extraWorkAmount,
      extraWorkGst: r.extraWorkGst,

      tdsRate: r.tdsRate,
      tdsManualAmount: r.tdsManualAmount,
      tdsAmount: r.tds,

      deductionAmount: r.deduction,
      retentionAmount: r.retention,
      penaltyAmount: r.manualPenalty,

      dashboardPenalty: r.dashboardPenalty,
      ceoPenaltyApproved: Boolean(r.ceoPenaltyApproved),
      finalPenaltyAmount: r.finalPenaltyAmount,

      payableBeforeDeduction: r.payableBeforeDeduction,
      netPayable: r.net,
      outstandingAmount: r.outstandingAmount,

      accountingVerifiedBy: me.uid || "",
      accountingVerifiedAt: serverTimestamp(),
    });

    await queueMail({
      to: ACCOUNTS_EMAIL,
      subject: `RA Bill Verified - ${invoiceNo}`,
      type: "RA_BILL_ACCOUNTANT_VERIFIED",
      body: `RA Bill ${invoiceNo} verified by Accountant.

Tender without GST: ${INR(r.tenderAmountWithoutGst)}
L1 without GST: ${INR(r.l1AmountWithoutGst)}
L1 GST 18% extra: ${INR(r.l1GstAmount)}

RA basic without GST: ${INR(r.basic)}
L1 cut without GST: ${INR(r.l1Cut)}
Approved basic: ${INR(r.approvedBasicAfterL1)}
GST on approved basic: ${INR(r.gstOnApprovedBasic)}

Extra work: ${INR(r.extraWorkAmount)}
TDS: ${INR(r.tds)}
Dashboard penalty: ${INR(r.dashboardPenalty)}
Final CEO penalty: ${INR(r.finalPenaltyAmount)}
Net payable: ${INR(r.net)}

Sent for CEO approval.`,
      data: { raBillId: r.id, tenderId: r.tenderId, vendorUid: r.vendorUid },
    });

    toast.success("Verified. CEO Approval pending.");
    load();
  };

  const ceoApprove = async (r) => {
    if (!["CEO", "ADMIN"].includes(me.role)) {
      toast.error("Only CEO/Admin allowed.");
      return;
    }

    if (r.accountingStatus !== "ACCOUNTANT_VERIFIED") {
      toast.error("Pehle Accountant verify kare.");
      return;
    }

    await updateDoc(doc(db, "ra_bills", r.id), {
      accountingStatus: "CEO_APPROVED",
      ceoApprovedBy: me.uid || "",
      ceoApprovedAt: serverTimestamp(),
      ledgerStatus: "INVOICE_LEDGER_POSTED",
      paymentStatus: r.paymentStatus || "PENDING",
      netPayable: r.net,
      outstandingAmount: Math.max(r.net - r.paidAmount, 0),
    });

    await safeAddLedger({
      vendorUid: r.vendorUid || "",
      vendorName: r.contractorName || "",
      tenderId: r.tenderId || "",
      raBillId: r.id,

      voucherNo: r.raInvoiceNo || r.displayBillNo,
      invoiceNo: r.raInvoiceNo || r.displayBillNo,
      type: "RA_BILL_INVOICE",

      debit: r.net,
      credit: 0,
      invoiceDate: r.raInvoiceDate || r.invoiceDate || "",

      taxableAmount: r.approvedBasicAfterL1,
      gstRate: GST_RATE,
      gstAmount: r.gstOnApprovedBasic,
      grossAmount: r.approvedBasicAfterL1 + r.gstOnApprovedBasic,

      tenderAmountWithoutGst: r.tenderAmountWithoutGst,
      l1AmountWithoutGst: r.l1AmountWithoutGst,
      l1GstAmount: r.l1GstAmount,
      l1WithGst: r.l1WithGst,

      originalBasicAmount: r.basic,
      l1CutAmount: r.l1Cut,
      approvedBasicAfterL1: r.approvedBasicAfterL1,

      extraWorkAmount: r.extraWorkAmount,
      extraWorkGst: r.extraWorkGst,

      tdsRate: r.tdsRate,
      tdsAmount: r.tds,

      deductionAmount: r.deduction,
      retentionAmount: r.retention,

      manualPenaltyAmount: r.manualPenalty,
      dashboardPenalty: r.dashboardPenalty,
      ceoPenaltyApproved: Boolean(r.ceoPenaltyApproved),
      finalPenaltyAmount: r.finalPenaltyAmount,

      payableBeforeDeduction: r.payableBeforeDeduction,
      netPayable: r.net,

      ledgerBreakup: {
        tenderAmountWithoutGst: r.tenderAmountWithoutGst,
        l1AmountWithoutGst: r.l1AmountWithoutGst,
        l1GstAmount: r.l1GstAmount,
        l1WithGst: r.l1WithGst,

        originalBasicAmount: r.basic,
        l1CutWithoutGst: r.l1Cut,
        approvedBasicAfterL1: r.approvedBasicAfterL1,
        gstOnApprovedBasic: r.gstOnApprovedBasic,

        extraWorkAmount: r.extraWorkAmount,
        extraWorkGst: r.extraWorkGst,

        tdsRate: r.tdsRate,
        tdsAmount: r.tds,

        deductionAmount: r.deduction,
        retentionAmount: r.retention,

        manualPenaltyAmount: r.manualPenalty,
        dashboardPenalty: r.dashboardPenalty,
        finalPenaltyAmount: r.finalPenaltyAmount,

        payableBeforeDeduction: r.payableBeforeDeduction,
        netPayable: r.net,
      },

      narration: `RA Bill Invoice approved by CEO - ${r.pacsName}. Basic: ${INR(
        r.basic
      )}, L1 Cut: ${INR(r.l1Cut)}, GST: ${INR(
        r.gstOnApprovedBasic
      )}, Extra Work: ${INR(r.extraWorkAmount)}, TDS: ${INR(
        r.tds
      )}, Penalty: ${INR(r.finalPenaltyAmount + r.manualPenalty)}, Net: ${INR(r.net)}`,

      serviceName: "RA Bill",
      status: "POSTED",
    });

    await queueMail({
      to: [ACCOUNTS_EMAIL, r.contractorEmail].filter(Boolean).join(","),
      subject: `RA Bill CEO Approved - ${r.raInvoiceNo || r.displayBillNo}`,
      type: "RA_BILL_CEO_APPROVED",
      body: `RA Bill approved by CEO.

Invoice: ${r.raInvoiceNo || r.displayBillNo}
Net Payable: ${INR(r.net)}
L1 Cut: ${INR(r.l1Cut)}
TDS: ${INR(r.tds)}
Penalty: ${INR(r.finalPenaltyAmount + r.manualPenalty)}

Accountant may process payment.`,
      data: { raBillId: r.id, tenderId: r.tenderId, vendorUid: r.vendorUid },
    });

    toast.success("CEO approved. Invoice debit ledger posted.");
    load();
  };

  const postFinalPenaltyToLedger = async (r) => {
    if (!["CEO", "ADMIN"].includes(me.role)) {
      toast.error("Only CEO/Admin allowed.");
      return;
    }

    const penaltyToPost = num(r.finalPenaltyAmount || 0);

    if (penaltyToPost <= 0) {
      toast.error("Final penalty amount zero hai.");
      return;
    }

    if (!r.ceoPenaltyApproved) {
      toast.error("Pehle CEO penalty approve/adjust kare.");
      return;
    }

    if (r.finalPenaltyLedgerPosted) {
      toast.error("Penalty already ledger me posted hai.");
      return;
    }

    const voucherNo = `PENALTY-${String(r.raInvoiceNo || r.displayBillNo).replace(/[\/\\]/g, "-")}`;

    await updateDoc(doc(db, "ra_bills", r.id), {
      finalPenaltyLedgerPosted: true,
      finalPenaltyLedgerVoucherNo: voucherNo,
      finalPenaltyLedgerPostedAt: serverTimestamp(),
      finalPenaltyLedgerPostedBy: me.uid || "",
    });

    await safeAddLedger({
      vendorUid: r.vendorUid || "",
      vendorName: r.contractorName || "",
      tenderId: r.tenderId || "",
      raBillId: r.id,

      voucherNo,
      invoiceNo: r.raInvoiceNo || r.displayBillNo,
      type: "RA_BILL_FINAL_PENALTY_CUT",

      debit: 0,
      credit: penaltyToPost,
      penaltyDate: toDateInput(new Date()),
      penaltyAmount: penaltyToPost,
      adjustedAgainstInvoice: r.raInvoiceNo || r.displayBillNo,

      narration: `Final delay penalty cut posted against RA bill ${
        r.raInvoiceNo || r.displayBillNo
      }. Penalty: ${INR(penaltyToPost)}`,
      serviceName: "Final Bill Penalty Cut",
      status: "POSTED",
    });

    await queueMail({
      to: [ACCOUNTS_EMAIL, r.contractorEmail].filter(Boolean).join(","),
      subject: `Final Penalty Posted - ${r.raInvoiceNo || r.displayBillNo}`,
      type: "RA_BILL_FINAL_PENALTY_POSTED",
      body: `Final penalty has been posted.

Invoice: ${r.raInvoiceNo || r.displayBillNo}
Penalty Amount: ${INR(penaltyToPost)}
Net Payable after penalty: ${INR(r.net)}`,
      data: { raBillId: r.id, tenderId: r.tenderId, vendorUid: r.vendorUid },
    });

    toast.success("Penalty posted to ledger.");
    load();
  };

  const accountantVerifyPayment = async (r) => {
    if (!["ACCOUNTANT", "ADMIN"].includes(me.role)) {
      toast.error("Only Accountant/Admin allowed.");
      return;
    }

    if (r.accountingStatus !== "CEO_APPROVED") {
      toast.error("Pehle CEO approve kare.");
      return;
    }

    const maxPayable = Math.max(r.net - r.paidAmount, 0);

    const utr = window.prompt("Payment UTR / Reference No:", r.paymentUtr || "");
    if (!utr) return;

    const paymentDate = window.prompt("Payment Date YYYY-MM-DD:", toDateInput(new Date()));
    if (!paymentDate) return;

    const amountText = window.prompt("Payment Amount:", String(maxPayable));
    if (!amountText) return;

    const amount = num(amountText);

    if (amount > maxPayable) {
      toast.error(`Payment net outstanding se jyada nahi ho sakta. Max: ${INR(maxPayable)}`);
      return;
    }

    const voucherNo = makePaymentVoucher(r);
    const newPaid = r.paidAmount + amount;
    const outstandingAfter = Math.max(r.net - newPaid, 0);

    await updateDoc(doc(db, "ra_bills", r.id), {
      paymentStatus: outstandingAfter <= 0 ? "FULLY_PAID" : "PART_PAID",
      paymentAmount: amount,
      totalPaidAmount: newPaid,
      allocatedPaidAmount: newPaid,
      outstandingAmount: outstandingAfter,
      paymentUtr: utr,
      lastPaymentUtr: utr,
      paymentDate,
      lastPaymentDate: paymentDate,
      lastPaymentVoucherNo: voucherNo,
      paymentVerifiedBy: me.uid || "",
      paymentVerifiedAt: serverTimestamp(),
      paymentLedgerStatus: "PAYMENT_LEDGER_POSTED",
    });

    await safeAddLedger({
      vendorUid: r.vendorUid || "",
      vendorName: r.contractorName || "",
      tenderId: r.tenderId || "",
      raBillId: r.id,
      voucherNo,
      referenceNo: utr,
      type: "RA_BILL_PAYMENT",
      debit: 0,
      credit: amount,
      paymentDate,
      paymentUtr: utr,
      adjustedAgainstInvoice: r.raInvoiceNo || r.displayBillNo,
      invoiceNo: r.raInvoiceNo || r.displayBillNo,
      narration: `RA Bill payment made against invoice ${r.raInvoiceNo || r.displayBillNo}`,
      serviceName: "RA Bill Payment",
      status: "POSTED",
    });

    await queueMail({
      to: [ACCOUNTS_EMAIL, r.contractorEmail].filter(Boolean).join(","),
      subject: `RA Bill Payment Verified - ${r.raInvoiceNo || r.displayBillNo}`,
      type: "RA_BILL_PAYMENT_VERIFIED",
      body: `Payment verified.

Invoice: ${r.raInvoiceNo || r.displayBillNo}
Amount: ${INR(amount)}
UTR: ${utr}
Outstanding after payment: ${INR(outstandingAfter)}`,
      data: { raBillId: r.id, tenderId: r.tenderId, vendorUid: r.vendorUid },
    });

    toast.success("Payment verified. Ledger posted.");
    load();
  };

  const openPdf = (r) => {
    if (!r.displayPdfUrl) return toast.error("RA Bill PDF available nahi hai");
    window.open(r.displayPdfUrl, "_blank");
  };

  const downloadPdf = (r) => {
    if (!r.displayPdfUrl) return toast.error("RA Bill PDF available nahi hai");
    const a = document.createElement("a");
    a.href = r.displayPdfUrl;
    a.download = `${r.displayBillNo}.pdf`;
    a.target = "_blank";
    a.click();
  };

  const regeneratePdf = async (r) => {
    try {
      const pdfBlob = generateRaPdfBlob(r.tender, r);
      const cleanBillNo = String(r.displayBillNo || "RA_Bill").replace(/[\/\\]/g, "-");

      const filePath = `ra_bills/${r.tenderId}/${r.vendorUid || "unknown"}/${cleanBillNo}_${r.id}.pdf`;
      const fileRef = ref(storage, filePath);

      await uploadBytes(fileRef, pdfBlob, { contentType: "application/pdf" });
      const pdfUrl = await getDownloadURL(fileRef);

      await updateDoc(doc(db, "ra_bills", r.id), {
        raBillNo: r.displayBillNo,
        raBillPdfUrl: pdfUrl,
        raBillPdfPath: filePath,
        raBillPdfName: `${cleanBillNo}.pdf`,
        raBillPdfRegeneratedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      toast.success("RA Bill PDF regenerated");
      load();
    } catch (e) {
      console.error(e);
      toast.error("PDF regenerate failed");
    }
  };

  const deleteFileIfExists = async (path) => {
    if (!path) return;
    try {
      await deleteObject(ref(storage, path));
    } catch (e) {
      console.warn("Storage delete skipped:", path, e.message);
    }
  };

  const deleteRaBill = async (r) => {
    if (me.role !== "CEO") {
      toast.error("Only CEO can delete RA Bill");
      return;
    }

    if (
      r.accountingStatus === "CEO_APPROVED" ||
      r.paymentStatus === "PAYMENT_VERIFIED" ||
      r.paymentStatus === "FULLY_PAID" ||
      r.paymentStatus === "PART_PAID"
    ) {
      toast.error("Approved/payment RA Bill delete nahi ho sakta.");
      return;
    }

    const ok = window.confirm(`Delete ${r.displayBillNo}?`);
    if (!ok) return;

    await deleteFileIfExists(r.raBillPdfPath);
    await deleteFileIfExists(r.approvedRaBillPdfPath);
    await deleteFileIfExists(r.gstInvoicePath);
    await deleteDoc(doc(db, "ra_bills", r.id));

    toast.success("RA Bill deleted.");
    setSelectedBill(null);
    load();
  };
    return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
        <Card title="Projects" value={totals.projects} />
        <Card title="RA Bills" value={totals.bills} />
        <Card title="Basic" value={INR(totals.basic)} />
        <Card title="Approved Basic" value={INR(totals.approvedBasic)} green />
        <Card title="TDS" value={INR(totals.tds)} red />
        <Card title="Penalty" value={INR(totals.finalPenalty)} red />
        <Card title="Paid" value={INR(totals.paid)} green />
        <Card title="Outstanding" value={INR(totals.outstanding)} red />
      </div>

      <div className="bg-white rounded-2xl border p-4">
        <div className="flex flex-wrap justify-between gap-3 items-center">
          <div>
            <h2 className="font-black text-lg">RA Bill Control Center</h2>
            <p className="text-xs text-slate-500">
              L1 ₹79.20L without GST cap • GST 18% extra • TDS only without GST • CEO penalty control • Voucher history
            </p>
          </div>

          <div className="flex gap-2">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="border rounded-xl px-4 py-2 w-80 text-sm"
              placeholder="Search bill / contractor / voucher / UTR..."
            />

            <button
              onClick={() => {
                setSelectedTenderId("");
                setSelectedVendorUid("");
              }}
              className="btn-dark"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {!selectedTenderId && (
        <div className="bg-white rounded-2xl border p-4">
          <h2 className="font-black mb-3">Step 1: Project Select</h2>

          <div className="overflow-auto">
            <table className="min-w-[1450px] w-full text-[11px] border">
              <thead className="bg-slate-900 text-white">
                <tr>
                  <th className="p-2 border text-left">Project</th>
                  <th className="p-2 border text-right">Tender Without GST</th>
                  <th className="p-2 border text-right">L1 Without GST</th>
                  <th className="p-2 border text-right">L1 GST 18%</th>
                  <th className="p-2 border text-right">L1 With GST</th>
                  <th className="p-2 border text-center">Bills</th>
                  <th className="p-2 border text-right">Approved Basic</th>
                  <th className="p-2 border text-right">L1 Cut</th>
                  <th className="p-2 border text-right">TDS</th>
                  <th className="p-2 border text-right">Penalty</th>
                  <th className="p-2 border text-right">Paid</th>
                  <th className="p-2 border text-right">Outstanding</th>
                  <th className="p-2 border text-center">Open</th>
                </tr>
              </thead>

              <tbody>
                {projectRows.map((p) => (
                  <tr key={p.tenderId} className="hover:bg-emerald-50">
                    <td className="p-2 border font-black">
                      {p.pacsName}
                      <div className="text-[10px] text-slate-500">Tender ID: {p.tenderId}</div>
                    </td>
                    <td className="p-2 border text-right">{INR(p.tenderAmountWithoutGst)}</td>
                    <td className="p-2 border text-right font-black">{INR(p.l1AmountWithoutGst)}</td>
                    <td className="p-2 border text-right text-blue-700 font-black">{INR(p.l1GstAmount)}</td>
                    <td className="p-2 border text-right text-blue-700 font-black">{INR(p.l1WithGst)}</td>
                    <td className="p-2 border text-center">{p.raCount}</td>
                    <td className="p-2 border text-right text-emerald-700 font-black">{INR(p.approvedBasic)}</td>
                    <td className="p-2 border text-right text-red-600 font-black">{INR(p.l1Cut)}</td>
                    <td className="p-2 border text-right text-red-600 font-black">{INR(p.tds)}</td>
                    <td className="p-2 border text-right text-red-600 font-black">{INR(p.finalPenalty)}</td>
                    <td className="p-2 border text-right text-emerald-700 font-black">{INR(p.paid)}</td>
                    <td className="p-2 border text-right text-red-600 font-black">{INR(p.outstanding)}</td>
                    <td className="p-2 border text-center">
                      <button
                        className="btn"
                        onClick={() => {
                          setSelectedTenderId(p.tenderId);
                          setSelectedVendorUid("");
                        }}
                      >
                        Open
                      </button>
                    </td>
                  </tr>
                ))}

                {!projectRows.length && (
                  <tr>
                    <td colSpan={13} className="p-8 text-center text-slate-500">
                      No projects found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedTenderId && !selectedVendorUid && (
        <div className="bg-white rounded-2xl border p-4">
          <div className="flex justify-between mb-3">
            <h2 className="font-black">Step 2: Contractor Select</h2>
            <button className="btn-gray" onClick={() => setSelectedTenderId("")}>
              Back
            </button>
          </div>

          <table className="w-full text-[11px] border">
            <thead className="bg-slate-900 text-white">
              <tr>
                <th className="p-2 border text-left">Contractor</th>
                <th className="p-2 border text-center">RA Bills</th>
                <th className="p-2 border text-right">L1 Without GST</th>
                <th className="p-2 border text-right">Net</th>
                <th className="p-2 border text-right">Paid</th>
                <th className="p-2 border text-right">Outstanding</th>
                <th className="p-2 border text-center">Open</th>
              </tr>
            </thead>

            <tbody>
              {contractorRows.map((c) => (
                <tr key={c.vendorUid} className="hover:bg-blue-50">
                  <td className="p-2 border font-black">{c.contractorName}</td>
                  <td className="p-2 border text-center">{c.raCount}</td>
                  <td className="p-2 border text-right font-black">{INR(c.l1AmountWithoutGst)}</td>
                  <td className="p-2 border text-right">{INR(c.net)}</td>
                  <td className="p-2 border text-right text-emerald-700 font-black">{INR(c.paid)}</td>
                  <td className="p-2 border text-right text-red-600 font-black">{INR(c.outstanding)}</td>
                  <td className="p-2 border text-center">
                    <button className="btn" onClick={() => setSelectedVendorUid(c.vendorUid)}>
                      Open Bills
                    </button>
                  </td>
                </tr>
              ))}

              {!contractorRows.length && (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-slate-500">
                    No contractors found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {selectedTenderId && selectedVendorUid && (
        <div className="bg-white rounded-2xl border p-4">
          <div className="flex justify-between mb-3">
            <h2 className="font-black">Step 3: RA Bill Register</h2>
            <button className="btn-gray" onClick={() => setSelectedVendorUid("")}>
              Back
            </button>
          </div>

          <div className="overflow-auto">
            <table className="min-w-[2300px] w-full text-[10.5px] border">
              <thead className="bg-slate-900 text-white">
                <tr>
                  <th className="p-2 border text-left">Invoice</th>
                  <th className="p-2 border text-right">Basic</th>
                  <th className="p-2 border text-right">L1 Cut</th>
                  <th className="p-2 border text-right">Approved Basic</th>
                  <th className="p-2 border text-right">GST</th>
                  <th className="p-2 border text-right">Extra Work</th>
                  <th className="p-2 border text-right">Before Deduction</th>
                  <th className="p-2 border text-right">TDS</th>
                  <th className="p-2 border text-right">Penalty</th>
                  <th className="p-2 border text-right">Net</th>
                  <th className="p-2 border text-right">Paid</th>
                  <th className="p-2 border text-right">Outstanding</th>
                  <th className="p-2 border text-center">Voucher History</th>
                  <th className="p-2 border text-center">Voucher / GST</th>
                  <th className="p-2 border text-left">Accounting</th>
                  <th className="p-2 border text-center">Action</th>
                </tr>
              </thead>

              <tbody>
                {billRows.map((r) => {
                  const voucherAvailable =
                    r.gstInvoiceUrl || r.invoicePdfUrl || r.raBillPdfUrl || r.displayPdfUrl;

                  return (
                    <tr key={r.id} className={r.l1Cut > 0 ? "bg-red-50" : ""}>
                      <td className="p-2 border font-black">
                        {r.raInvoiceNo || r.displayBillNo}
                        <div className="text-[10px] text-slate-500">{fmtDate(r.raInvoiceDate)}</div>
                        <div className="text-[10px] text-slate-500">ID: {r.id}</div>
                      </td>

                      <td className="p-2 border text-right font-black">{INR(r.basic)}</td>
                      <td className="p-2 border text-right text-red-600 font-black">
                        {INR(r.l1Cut)}
                        {r.l1Cut > 0 && <div className="text-[10px]">Auto</div>}
                      </td>
                      <td className="p-2 border text-right text-emerald-700 font-black">{INR(r.approvedBasicAfterL1)}</td>
                      <td className="p-2 border text-right text-blue-700 font-black">{INR(r.gstOnApprovedBasic)}</td>
                      <td className="p-2 border text-right text-emerald-700 font-black">
                        {INR(r.extraWorkAmount)}
                        {r.extraWorkGst > 0 && <div className="text-[10px] text-blue-600">GST {INR(r.extraWorkGst)}</div>}
                      </td>
                      <td className="p-2 border text-right font-black">{INR(r.payableBeforeDeduction)}</td>
                      <td className="p-2 border text-right text-red-600 font-black">
                        {INR(r.tds)}
                        <div className="text-[10px]">{r.tdsRate}%</div>
                      </td>
                      <td className="p-2 border text-right text-red-600 font-black">
                        {INR(r.finalPenaltyAmount + r.manualPenalty)}
                        {r.dashboardPenalty > 0 && (
                          <div className="text-[10px] text-slate-500">Dash {INR(r.dashboardPenalty)}</div>
                        )}
                        {r.ceoPenaltyApproved && <div className="text-[10px] text-emerald-700">CEO OK</div>}
                        {r.finalPenaltyLedgerPosted && <div className="text-[10px] text-emerald-700">Ledger</div>}
                      </td>
                      <td className="p-2 border text-right text-emerald-700 font-black">{INR(r.net)}</td>
                      <td className="p-2 border text-right text-emerald-700 font-black">{INR(r.paidAmount)}</td>
                      <td className="p-2 border text-right text-red-600 font-black">{INR(r.outstandingAmount)}</td>

                      <td className="p-2 border text-center">
                        {r.paymentHistory?.length ? (
                          <button className="btn-gray" onClick={() => setHistoryBill(r)}>
                            {r.paymentHistory.length} Voucher
                          </button>
                        ) : (
                          <span className="text-slate-400">No Payment</span>
                        )}
                      </td>

                      <td className="p-2 border text-center">
                        {voucherAvailable ? (
                          <span className="rounded-full bg-emerald-100 px-3 py-1 text-[10px] font-black text-emerald-700">
                            AVAILABLE
                          </span>
                        ) : (
                          <span className="rounded-full bg-red-100 px-3 py-1 text-[10px] font-black text-red-700">
                            MISSING
                          </span>
                        )}

                        {r.gstInvoiceUrl && (
                          <div className="mt-1">
                            <button className="btn-mini" onClick={() => window.open(r.gstInvoiceUrl, "_blank")}>
                              GST
                            </button>
                          </div>
                        )}
                      </td>

                      <td className="p-2 border font-bold">
                        {r.accountingStatus || "PENDING"}
                        <div className="text-[10px] text-slate-500">{r.paymentStatus || "PENDING"}</div>
                      </td>

                      <td className="p-2 border">
                        <div className="flex flex-wrap gap-2 justify-center">
                          <button className="btn-gray" onClick={() => setSelectedBill(r)}>Detail</button>
                          <button className="btn-gray" onClick={() => editOldInvoice(r)}>Edit</button>
                          <button className="btn-red" onClick={() => sendCorrection(r)}>Correction</button>

                          {["CEO", "ADMIN"].includes(me.role) && (
                            <button className="btn-red" onClick={() => approveOrAdjustPenaltyByCEO(r)}>
                              Penalty CEO
                            </button>
                          )}

                          {["ACCOUNTANT", "ADMIN"].includes(me.role) &&
                            r.accountingStatus !== "ACCOUNTANT_VERIFIED" &&
                            r.accountingStatus !== "CEO_APPROVED" && (
                              <button className="btn" onClick={() => accountantVerify(r)}>
                                Verify
                              </button>
                            )}

                          {["CEO", "ADMIN"].includes(me.role) &&
                            r.accountingStatus === "ACCOUNTANT_VERIFIED" && (
                              <button className="btn" onClick={() => ceoApprove(r)}>
                                CEO Approve
                              </button>
                            )}

                          {["CEO", "ADMIN"].includes(me.role) &&
                            r.ceoPenaltyApproved &&
                            r.finalPenaltyAmount > 0 &&
                            !r.finalPenaltyLedgerPosted && (
                              <button className="btn-red" onClick={() => postFinalPenaltyToLedger(r)}>
                                Post Penalty
                              </button>
                            )}

                          {["ACCOUNTANT", "ADMIN"].includes(me.role) &&
                            r.accountingStatus === "CEO_APPROVED" &&
                            r.outstandingAmount > 0 && (
                              <button className="btn-dark" onClick={() => accountantVerifyPayment(r)}>
                                Pay
                              </button>
                            )}

                          {me.role === "CEO" && r.accountingStatus !== "CEO_APPROVED" && (
                            <button className="btn-red" onClick={() => deleteRaBill(r)}>Delete</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}

                {!billRows.length && (
                  <tr>
                    <td colSpan={16} className="p-8 text-center text-slate-500">
                      No RA bills found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {historyBill && (
        <HistoryModal bill={historyBill} onClose={() => setHistoryBill(null)} />
      )}

      {selectedBill && (
        <BillModal
          bill={selectedBill}
          onClose={() => setSelectedBill(null)}
          alreadyApprovedBasicWithoutGst={getAlreadyApprovedBasicWithoutGst(selectedBill)}
          openPdf={openPdf}
          downloadPdf={downloadPdf}
          regeneratePdf={regeneratePdf}
          deleteRaBill={deleteRaBill}
          postFinalPenaltyToLedger={postFinalPenaltyToLedger}
          approveOrAdjustPenaltyByCEO={approveOrAdjustPenaltyByCEO}
          me={me}
        />
      )}

      <style>{`
        .btn{background:#059669;color:white;padding:7px 11px;border-radius:10px;font-weight:800;font-size:10px}
        .btn-dark{background:#0f172a;color:white;padding:7px 11px;border-radius:10px;font-weight:800;font-size:10px}
        .btn-red{background:#dc2626;color:white;padding:7px 11px;border-radius:10px;font-weight:800;font-size:10px}
        .btn-gray{background:#e2e8f0;color:#0f172a;padding:7px 11px;border-radius:10px;font-weight:800;font-size:10px}
        .btn-mini{background:#0f172a;color:white;padding:4px 8px;border-radius:8px;font-weight:800;font-size:9px}
      `}</style>
    </div>
  );
}

function HistoryModal({ bill, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-5xl w-full shadow-2xl overflow-hidden">
        <div className="bg-slate-900 text-white p-5 flex justify-between">
          <div>
            <h2 className="font-black text-lg">Payment Voucher History</h2>
            <p className="text-xs text-slate-300">{bill.raInvoiceNo || bill.displayBillNo}</p>
          </div>
          <button className="bg-white text-slate-900 rounded-lg px-3 py-1 font-black" onClick={onClose}>
            X
          </button>
        </div>

        <div className="p-5 overflow-auto">
          <table className="min-w-[850px] w-full text-xs border">
            <thead className="bg-slate-900 text-white">
              <tr>
                <th className="p-3 border">Sr</th>
                <th className="p-3 border text-left">Voucher</th>
                <th className="p-3 border text-center">Date</th>
                <th className="p-3 border text-left">UTR</th>
                <th className="p-3 border text-right">Invoice Amount</th>
                <th className="p-3 border text-right">Paid</th>
                <th className="p-3 border text-right">Balance</th>
                <th className="p-3 border text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {(bill.paymentHistory || []).map((h, i) => (
                <tr key={i}>
                  <td className="p-3 border text-center">{i + 1}</td>
                  <td className="p-3 border font-black">{h.voucherNo}</td>
                  <td className="p-3 border text-center">{fmtDate(h.paymentDate)}</td>
                  <td className="p-3 border">{h.paymentUtr || "—"}</td>
                  <td className="p-3 border text-right">{INR(h.invoiceAmount)}</td>
                  <td className="p-3 border text-right text-emerald-700 font-black">{INR(h.adjustedAmount)}</td>
                  <td className="p-3 border text-right text-red-600 font-black">{INR(h.outstandingAfter)}</td>
                  <td className="p-3 border text-center">{h.status || "—"}</td>
                </tr>
              ))}

              {!bill.paymentHistory?.length && (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-slate-500">
                    No voucher history found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function BillModal({
  bill,
  onClose,
  alreadyApprovedBasicWithoutGst,
  openPdf,
  downloadPdf,
  regeneratePdf,
  deleteRaBill,
  postFinalPenaltyToLedger,
  approveOrAdjustPenaltyByCEO,
  me,
}) {
  const basicBalance =
    bill.l1AmountWithoutGst -
    (alreadyApprovedBasicWithoutGst + bill.approvedBasicAfterL1);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-7xl w-full shadow-2xl max-h-[90vh] overflow-auto">
        <div className="p-5 border-b flex justify-between sticky top-0 bg-white z-10">
          <div>
            <h2 className="font-black text-xl">RA Bill Detail</h2>
            <p className="text-xs text-slate-500">{bill.raInvoiceNo || bill.displayBillNo}</p>
          </div>
          <button className="btn-dark" onClick={onClose}>Close</button>
        </div>

        <div className="p-5 grid md:grid-cols-5 gap-4 text-sm">
          <Detail label="Project" value={bill.pacsName} />
          <Detail label="Contractor" value={bill.contractorName} />
          <Detail label="Tender Without GST" value={INR(bill.tenderAmountWithoutGst)} />
          <Detail label="L1 Without GST" value={INR(bill.l1AmountWithoutGst)} />
          <Detail label="L1 With GST" value={INR(bill.l1WithGst)} />

          <Detail label="Original RA Basic" value={INR(bill.basic)} />
          <Detail label="L1 Cut" value={INR(bill.l1Cut)} red={bill.l1Cut > 0} />
          <Detail label="Approved Basic" value={INR(bill.approvedBasicAfterL1)} green />
          <Detail label="GST on Approved Basic" value={INR(bill.gstOnApprovedBasic)} />
          <Detail label="Basic Balance" value={INR(basicBalance)} red={basicBalance < 0} />

          <Detail label="Extra Work" value={INR(bill.extraWorkAmount)} green />
          <Detail label="Extra Work GST" value={INR(bill.extraWorkGst)} />
          <Detail label="Payable Before Deduction" value={INR(bill.payableBeforeDeduction)} />
          <Detail label={`TDS ${bill.tdsRate}%`} value={INR(bill.tds)} red />
          <Detail label="Other Deduction" value={INR(bill.deduction)} red />

          <Detail label="Retention" value={INR(bill.retention)} red />
          <Detail label="Manual Penalty" value={INR(bill.manualPenalty)} red />
          <Detail label="Dashboard Penalty" value={INR(bill.dashboardPenalty)} red />
          <Detail label="CEO Final Penalty" value={INR(bill.finalPenaltyAmount)} red />
          <Detail label="Net Payable" value={INR(bill.net)} green />

          <Detail label="Paid" value={INR(bill.paidAmount)} green />
          <Detail label="Outstanding" value={INR(bill.outstandingAmount)} red />
          <Detail label="Accounting" value={bill.accountingStatus || "PENDING"} />
          <Detail label="Payment" value={bill.paymentStatus || "PENDING"} />
          <Detail label="Penalty Ledger" value={bill.finalPenaltyLedgerPosted ? "POSTED" : "NOT POSTED"} />
        </div>

        <div className="p-5 border-t flex flex-wrap gap-3">
          {bill.gstInvoiceUrl && (
            <button className="btn-gray" onClick={() => window.open(bill.gstInvoiceUrl, "_blank")}>
              View GST Invoice
            </button>
          )}

          {bill.displayPdfUrl ? (
            <>
              <button className="btn-gray" onClick={() => openPdf(bill)}>
                View RA Bill PDF
              </button>
              <button className="btn-dark" onClick={() => downloadPdf(bill)}>
                Download RA Bill PDF
              </button>
            </>
          ) : (
            <button className="btn-dark" onClick={() => regeneratePdf(bill)}>
              Regenerate RA Bill PDF
            </button>
          )}

          {["CEO", "ADMIN"].includes(me.role) && (
            <button className="btn-red" onClick={() => approveOrAdjustPenaltyByCEO(bill)}>
              CEO Penalty Adjust
            </button>
          )}

          {["CEO", "ADMIN"].includes(me.role) &&
            bill.ceoPenaltyApproved &&
            bill.finalPenaltyAmount > 0 &&
            !bill.finalPenaltyLedgerPosted && (
              <button className="btn-red" onClick={() => postFinalPenaltyToLedger(bill)}>
                Post Penalty Ledger
              </button>
            )}

          {me.role === "CEO" && bill.accountingStatus !== "CEO_APPROVED" && (
            <button className="btn-red" onClick={() => deleteRaBill(bill)}>
              Delete RA Bill
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function generateRaPdfBlob(tender, bill) {
  const pdf = new jsPDF("p", "mm", "a4");
  const pageWidth = pdf.internal.pageSize.getWidth();

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(14);
  pdf.text("Running Account Bill", pageWidth / 2, 15, { align: "center" });

  autoTable(pdf, {
    startY: 28,
    head: [["Particular", "Amount"]],
    body: [
      ["Tender Amount Without GST", INR(bill.tenderAmountWithoutGst)],
      ["L1 Amount Without GST", INR(bill.l1AmountWithoutGst)],
      ["L1 With GST", INR(bill.l1WithGst)],
      ["Original Basic Bill", INR(bill.basic)],
      ["L1 Cut Without GST", INR(bill.l1Cut)],
      ["Approved Basic", INR(bill.approvedBasicAfterL1)],
      ["GST on Approved Basic", INR(bill.gstOnApprovedBasic)],
      ["Extra Work", INR(bill.extraWorkAmount)],
      ["Extra Work GST", INR(bill.extraWorkGst)],
      ["TDS", INR(bill.tds)],
      ["Penalty", INR(bill.finalPenaltyAmount + bill.manualPenalty)],
      ["Net Payable", INR(bill.net)],
    ],
    theme: "grid",
    styles: { fontSize: 8 },
  });

  return pdf.output("blob");
}

function Detail({ label, value, green, red }) {
  return (
    <div className="border rounded-xl p-3">
      <p className="text-xs text-slate-500 font-bold">{label}</p>
      <p
        className={`font-black mt-1 ${
          green ? "text-emerald-700" : red ? "text-red-600" : ""
        }`}
      >
        {value}
      </p>
    </div>
  );
}

function Card({ title, value, green, red }) {
  return (
    <div className="bg-white rounded-2xl border p-4 shadow-sm">
      <p className="text-xs text-slate-500">{title}</p>
      <h2
        className={`text-lg font-black mt-2 ${
          green ? "text-emerald-700" : red ? "text-red-600" : "text-slate-900"
        }`}
      >
        {value}
      </h2>
    </div>
  );
}